import React, { useState, useEffect } from 'react'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { Heading, Text, Button, Input, LoadingSpinner } from '../components/ui'
import { BRAND_COLOR } from '../constants'
import { 
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  listTags,
  createTag,
  updateTag,
  deleteTag,
  getWikiManagementStats,
  getMostViewedArticles
} from '../services/wiki'
import { WikiCategory, WikiDepartment, WikiTag, CreateCategoryPayload, CreateDepartmentPayload, CreateTagPayload } from '../types/wiki'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  FolderIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  TagIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline'

const WikiGerenciar: React.FC = () => {
  // Hooks de permissões
  const { hasPermission: canInsert } = usePermission('wiki_gerenciar', 'insert')
  const { hasPermission: canUpdate } = usePermission('wiki_gerenciar', 'update')
  const { hasPermission: canDelete } = usePermission('wiki_gerenciar', 'delete')
  
  // Estados principais
  const [categories, setCategories] = useState<WikiCategory[]>([])
  const [departments, setDepartments] = useState<WikiDepartment[]>([])
  const [tags, setTags] = useState<WikiTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  // Estados de abas
  const [activeTab, setActiveTab] = useState<'cadastros' | 'indicadores'>('cadastros')

  // Estados de modais
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [showDepartmentModal, setShowDepartmentModal] = useState(false)
  const [showTagModal, setShowTagModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<WikiCategory | null>(null)
  const [editingDepartment, setEditingDepartment] = useState<WikiDepartment | null>(null)
  const [editingTag, setEditingTag] = useState<WikiTag | null>(null)

  // Estados de formulário
  const [categoryForm, setCategoryForm] = useState<CreateCategoryPayload>({
    name: '',
    description: ''
  })
  const [departmentForm, setDepartmentForm] = useState<CreateDepartmentPayload>({
    name: '',
    description: '',
    color: '#3B82F6'
  })
  const [tagForm, setTagForm] = useState<CreateTagPayload>({
    name: ''
  })
  const [modalLoading, setModalLoading] = useState(false)

  // Estados de estatísticas
  const [stats, setStats] = useState<any>(null)
  const [mostViewedArticles, setMostViewedArticles] = useState<any[]>([])

  // Estados para seções expansíveis
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  // Função para buscar dados
  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [categoriesData, departmentsData, tagsData, statsData, mostViewedData] = await Promise.all([
        listCategories(),
        listDepartments(),
        listTags(),
        getWikiManagementStats(),
        getMostViewedArticles(10)
      ])

      setCategories(categoriesData)
      setDepartments(departmentsData)
      setTags(tagsData)
      setStats(statsData)
      setMostViewedArticles(mostViewedData)

      setRetryCount(0)
    } catch (err) {
      console.error('Erro ao carregar dados:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
      setRetryCount(prev => prev + 1)
    } finally {
      setLoading(false)
    }
  }

  // Função de retry
  const retryFetch = () => {
    if (retryCount < 3) {
      fetchData()
    }
  }

  // Função para alternar expansão das seções
  const toggleSectionExpansion = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  // Carregar dados iniciais
  useEffect(() => {
    fetchData()
  }, [])

  // Funções de modal - Categorias
  const openCategoryModal = (category?: WikiCategory) => {
    if (category) {
      if (!canUpdate) {
        alert('Você não tem permissão para editar categorias.')
        return
      }
      setEditingCategory(category)
      setCategoryForm({
        name: category.name,
        description: category.description || ''
      })
    } else {
      if (!canInsert) {
        alert('Você não tem permissão para criar categorias.')
        return
      }
      setEditingCategory(null)
      setCategoryForm({
        name: '',
        description: ''
      })
    }
    setShowCategoryModal(true)
  }

  const closeCategoryModal = () => {
    setShowCategoryModal(false)
    setEditingCategory(null)
    setCategoryForm({
      name: '',
      description: ''
    })
  }

  const handleSaveCategory = async () => {
    try {
      setModalLoading(true)
      
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryForm)
      } else {
        await createCategory(categoryForm)
      }
      
      await fetchData()
      closeCategoryModal()
    } catch (err) {
      console.error('Erro ao salvar categoria:', err)
    } finally {
      setModalLoading(false)
    }
  }

  const handleDeleteCategory = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir categorias.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await deleteCategory(id)
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir categoria:', err)
      }
    }
  }

  // Funções de modal - Departamentos
  const openDepartmentModal = (department?: WikiDepartment) => {
    if (department) {
      if (!canUpdate) {
        alert('Você não tem permissão para editar departamentos.')
        return
      }
      setEditingDepartment(department)
      setDepartmentForm({
        name: department.name,
        description: department.description || '',
        color: department.color || '#3B82F6'
      })
    } else {
      if (!canInsert) {
        alert('Você não tem permissão para criar departamentos.')
        return
      }
      setEditingDepartment(null)
      setDepartmentForm({
        name: '',
        description: '',
        color: '#3B82F6'
      })
    }
    setShowDepartmentModal(true)
  }

  const closeDepartmentModal = () => {
    setShowDepartmentModal(false)
    setEditingDepartment(null)
    setDepartmentForm({
      name: '',
      description: '',
      color: '#3B82F6'
    })
  }

  const handleSaveDepartment = async () => {
    try {
      setModalLoading(true)
      
      if (editingDepartment) {
        await updateDepartment(editingDepartment.id, departmentForm)
      } else {
        await createDepartment(departmentForm)
      }
      
      await fetchData()
      closeDepartmentModal()
    } catch (err) {
      console.error('Erro ao salvar departamento:', err)
    } finally {
      setModalLoading(false)
    }
  }

  const handleDeleteDepartment = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir departamentos.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir este departamento?')) {
      try {
        await deleteDepartment(id)
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir departamento:', err)
      }
    }
  }

  // Funções de modal - Tags
  const openTagModal = (tag?: WikiTag) => {
    if (tag) {
      if (!canUpdate) {
        alert('Você não tem permissão para editar tags.')
        return
      }
      setEditingTag(tag)
      setTagForm({
        name: tag.name
      })
    } else {
      if (!canInsert) {
        alert('Você não tem permissão para criar tags.')
        return
      }
      setEditingTag(null)
      setTagForm({
        name: ''
      })
    }
    setShowTagModal(true)
  }

  const closeTagModal = () => {
    setShowTagModal(false)
    setEditingTag(null)
    setTagForm({
      name: ''
    })
  }

  const handleSaveTag = async () => {
    try {
      setModalLoading(true)
      
      if (editingTag) {
        await updateTag(editingTag.id, tagForm)
      } else {
        await createTag(tagForm)
      }
      
      await fetchData()
      closeTagModal()
    } catch (err) {
      console.error('Erro ao salvar tag:', err)
    } finally {
      setModalLoading(false)
    }
  }

  const handleDeleteTag = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir tags.')
      return
    }

    if (window.confirm('Tem certeza que deseja excluir esta tag?')) {
      try {
        await deleteTag(id)
        await fetchData()
      } catch (err) {
        console.error('Erro ao excluir tag:', err)
      }
    }
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto mb-4" />
              <Text size="2xl" className="text-gray-600 dark:text-gray-400 mb-2">
                Carregando Gerenciamento...
              </Text>
              {retryCount > 0 && (
                <Text size="sm" className="text-gray-500 dark:text-gray-500">
                  Tentativa {retryCount} de 3...
                </Text>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <Text size="2xl" className="text-red-600 dark:text-red-400 mb-2">
                Erro ao carregar Gerenciamento
              </Text>
              <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
                {error}
              </Text>
              <Button
                onClick={retryFetch}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Tentar Novamente
              </Button>
              {retryCount > 0 && (
                <Text size="xs" className="text-gray-500 dark:text-gray-500 mt-2">
                  Já tentamos {retryCount} vezes
                </Text>
              )}
            </div>
          </div>
        ) : (
          <PermissionGuard 
            moduleName="wiki_gerenciar" 
            permissionType="show"
            fallback={
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Text size="2xl" className="text-gray-500 dark:text-gray-400 mb-2">
                    Acesso Negado
                  </Text>
                  <Text size="sm" color="muted">
                    Você não tem permissão para acessar o Gerenciamento da Wiki.
                  </Text>
                </div>
              </div>
            }
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    Gerenciar Wiki
                  </Heading>
                  <Text size="sm" color="muted" className="mt-2">
                    Gerencie categorias, departamentos e visualize indicadores
                  </Text>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('cadastros')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'cadastros'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Cadastros
                </button>
                <button
                  onClick={() => setActiveTab('indicadores')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'indicadores'
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Indicadores
                </button>
              </nav>
            </div>

            {/* Conteúdo das Abas */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'cadastros' ? (
                <div className="space-y-6">
                  {/* Seção Categorias */}
                  <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-md bg-white dark:bg-gray-800">
                    {/* Header da Seção */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                      onClick={() => toggleSectionExpansion('categorias')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <FolderIcon className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-gray-900 dark:text-white text-lg">
                              Categorias
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {categories.length} registros
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {expandedSections.has('categorias') ? 'Retrair' : 'Expandir'}
                          </div>
                          {expandedSections.has('categorias') ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Conteúdo Expansível */}
                    {expandedSections.has('categorias') && (
                      <div className="border-t-2 border-gray-300 dark:border-gray-600 transition-all duration-300 bg-gray-50 dark:bg-gray-700/30">
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-6">
                            <Button
                              onClick={() => openCategoryModal()}
                              className="flex items-center gap-2"
                              style={{ backgroundColor: BRAND_COLOR }}
                            >
                              <PlusIcon className="h-5 w-5" />
                              Nova Categoria
                            </Button>
                          </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Nome
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Descrição
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {categories.map((category) => (
                            <tr key={category.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {category.name}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                {category.description || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <Button
                                    onClick={() => openCategoryModal(category)}
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                    Editar
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteCategory(category.id)}
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                    Excluir
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Seção Departamentos */}
                  <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-md bg-white dark:bg-gray-800">
                    {/* Header da Seção */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                      onClick={() => toggleSectionExpansion('departments')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <BuildingOfficeIcon className="h-5 w-5 text-green-600" />
                            <span className="font-medium text-gray-900 dark:text-white text-lg">
                              Departamentos
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              {departments.length} registros
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {expandedSections.has('departments') ? 'Retrair' : 'Expandir'}
                          </div>
                          {expandedSections.has('departments') ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Conteúdo Expansível */}
                    {expandedSections.has('departments') && (
                      <div className="border-t-2 border-gray-300 dark:border-gray-600 transition-all duration-300 bg-gray-50 dark:bg-gray-700/30">
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-6">
                            <Button
                              onClick={() => openDepartmentModal()}
                              className="flex items-center gap-2"
                              style={{ backgroundColor: BRAND_COLOR }}
                            >
                              <PlusIcon className="h-5 w-5" />
                              Novo Departamento
                            </Button>
                          </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Nome
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Descrição
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Cor
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {departments.map((department) => (
                            <tr key={department.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {department.name}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                {department.description || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded-full border border-gray-300"
                                    style={{ backgroundColor: department.color }}
                                  ></div>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {department.color}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <Button
                                    onClick={() => openDepartmentModal(department)}
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                    Editar
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteDepartment(department.id)}
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                    Excluir
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Seção Tags */}
                  <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-md bg-white dark:bg-gray-800">
                    {/* Header da Seção */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                      onClick={() => toggleSectionExpansion('tags')}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2">
                            <TagIcon className="h-5 w-5 text-purple-600" />
                            <span className="font-medium text-gray-900 dark:text-white text-lg">
                              Tags
                            </span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              {tags.length} registros
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {expandedSections.has('tags') ? 'Retrair' : 'Expandir'}
                          </div>
                          {expandedSections.has('tags') ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Conteúdo Expansível */}
                    {expandedSections.has('tags') && (
                      <div className="border-t-2 border-gray-300 dark:border-gray-600 transition-all duration-300 bg-gray-50 dark:bg-gray-700/30">
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-6">
                            <Button
                              onClick={() => openTagModal()}
                              className="flex items-center gap-2"
                              style={{ backgroundColor: BRAND_COLOR }}
                            >
                              <PlusIcon className="h-5 w-5" />
                              Nova Tag
                            </Button>
                          </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Nome
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Slug
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {tags.map((tag) => (
                            <tr key={tag.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {tag.name}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                {tag.slug}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <Button
                                    onClick={() => openTagModal(tag)}
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                    Editar
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteTag(tag.id)}
                                    size="sm"
                                    variant="outline"
                                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                    Excluir
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Cards de Estatísticas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <ChartBarIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            Total de Artigos
                          </Text>
                          <Text size="2xl" className="font-semibold text-gray-900 dark:text-white">
                            {stats?.total_articles || 0}
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <FolderIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            Categorias
                          </Text>
                          <Text size="2xl" className="font-semibold text-gray-900 dark:text-white">
                            {stats?.total_categories || 0}
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <BuildingOfficeIcon className="h-8 w-8 text-purple-600" />
                        </div>
                        <div className="ml-4">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            Departamentos
                          </Text>
                          <Text size="2xl" className="font-semibold text-gray-900 dark:text-white">
                            {stats?.total_departments || departments.length}
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <TagIcon className="h-8 w-8 text-indigo-600" />
                        </div>
                        <div className="ml-4">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            Tags
                          </Text>
                          <Text size="2xl" className="font-semibold text-gray-900 dark:text-white">
                            {tags.length}
                          </Text>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <EyeIcon className="h-8 w-8 text-orange-600" />
                        </div>
                        <div className="ml-4">
                          <Text size="sm" className="text-gray-500 dark:text-gray-400">
                            Total de Visualizações
                          </Text>
                          <Text size="2xl" className="font-semibold text-gray-900 dark:text-white">
                            {stats?.total_views || 0}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Artigos Mais Visualizados */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                      Artigos Mais Visualizados
                    </Heading>
                     <div className="space-y-3">
                       {mostViewedArticles && mostViewedArticles.length > 0 ? (
                         mostViewedArticles.map((article: any, index: number) => (
                           <div key={article.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                             <div className="flex items-center gap-3">
                               <span className="flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium">
                                 {index + 1}
                               </span>
                               <div>
                                 <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                                   {article.title}
                                 </Text>
                                 <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                   {article.category_name && article.department_name 
                                     ? `${article.category_name} • ${article.department_name}`
                                     : article.category_name || article.department_name || 'Sem categoria'
                                   }
                                 </Text>
                               </div>
                             </div>
                             <div className="flex items-center gap-2">
                               <EyeIcon className="h-4 w-4 text-gray-400" />
                               <Text size="sm" className="text-gray-600 dark:text-gray-400">
                                 {article.view_count} visualizações
                               </Text>
                             </div>
                           </div>
                         ))
                       ) : (
                         <div className="text-center py-8">
                           <EyeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                           <Text size="sm" className="text-gray-500 dark:text-gray-400">
                             Nenhum artigo foi visualizado ainda
                           </Text>
                         </div>
                       )}
                     </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
        )}
      </div>

      {/* Modal de Categoria */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </Heading>
              <Button
                onClick={closeCategoryModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <Text size="sm" className="mb-2">Nome</Text>
                  <Input
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Digite o nome da categoria..."
                  />
                </div>

                <div>
                  <Text size="sm" className="mb-2">Descrição</Text>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Digite a descrição da categoria..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeCategoryModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveCategory}
                disabled={modalLoading || !categoryForm.name.trim()}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingCategory ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Departamento */}
      {showDepartmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingDepartment ? 'Editar Departamento' : 'Novo Departamento'}
              </Heading>
              <Button
                onClick={closeDepartmentModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <Text size="sm" className="mb-2">Nome</Text>
                  <Input
                    value={departmentForm.name}
                    onChange={(e) => setDepartmentForm((prev: CreateDepartmentPayload) => ({ ...prev, name: e.target.value }))}
                    placeholder="Digite o nome do departamento..."
                  />
                </div>

                <div>
                  <Text size="sm" className="mb-2">Descrição</Text>
                  <textarea
                    value={departmentForm.description}
                    onChange={(e) => setDepartmentForm((prev: CreateDepartmentPayload) => ({ ...prev, description: e.target.value }))}
                    placeholder="Digite a descrição do departamento..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    rows={3}
                  />
                </div>

                <div>
                  <Text size="sm" className="mb-2">Cor</Text>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={departmentForm.color}
                      onChange={(e) => setDepartmentForm((prev: CreateDepartmentPayload) => ({ ...prev, color: e.target.value }))}
                      className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer"
                    />
                    <Input
                      value={departmentForm.color}
                      onChange={(e) => setDepartmentForm((prev: CreateDepartmentPayload) => ({ ...prev, color: e.target.value }))}
                      placeholder="#3B82F6"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeDepartmentModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveDepartment}
                disabled={modalLoading || !departmentForm.name.trim()}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingDepartment ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Tag */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingTag ? 'Editar Tag' : 'Nova Tag'}
              </Heading>
              <Button
                onClick={closeTagModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <Text size="sm" className="mb-2">Nome</Text>
                  <Input
                    value={tagForm.name}
                    onChange={(e) => setTagForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Digite o nome da tag..."
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeTagModal}
                variant="outline"
                disabled={modalLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveTag}
                disabled={modalLoading || !tagForm.name.trim()}
                className="flex items-center gap-2"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                {modalLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckIcon className="h-4 w-4" />
                    {editingTag ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WikiGerenciar
